import React, { Component, Fragment } from 'react';
import { Table } from 'semantic-ui-react';
import isEqual from 'lodash/isEqual';
import params from '../../utils/queryParams';

import {
  numberWithCommas,
  debounce,
  getNestedValue,
  checkNestedKeys
} from '../../utils/utils';

import  Card from '../Card/Card';
import  CSVExport from '../CSVExport/CSVExport';
import  SearchInput from '../SearchInput/SearchInput';
import  HeaderInfoTooltip from '../HeaderInfoTooltip/HeaderInfoTooltip';
import  TablePagination from '../TablePagination/TablePagination';

import FilterBox from './FilterBox';

const PAGE_SIZE = 50;
const DEFAULT_CONTENT_MIN_WIDTH = 1060;

export class DataTable extends Component {
  topOffset = 0;
  constructor(props) {
    super(props);

    this.tableRef = React.createRef();
    this.tableWrapperRef = React.createRef();
    this.tableTopScrollRef = React.createRef();

    const si = props.searchInput || {};
    // if there is an incoming search token, and the table name matches, set it
    const searchQuery =
      si.searchTerm && si.tableName === props.tableName ? si.searchTerm : '';

    let qpSearch = params.get('s');

    this.state = {
      needToSort: true,
      originalData: [],
      searchQuery: qpSearch ? decodeURIComponent(qpSearch) : searchQuery,
      searchedData: [],
      sortedData: [],
      pageNumber: 1,
      sortColumn: props.defaultSort,
      sortDirection: props.defaultSortDirection,
      shouldSendAnalytics: false,
      isFixedHeader: false,
      leftOffset: 0,
      isFiltered: false,
      totalCount: null,
      needsHorizontalScroll: false
    };
  }

  componentDidMount() {
    const { searchInput, tableName } = this.props;
    const sitName = (searchInput && searchInput.tableName) || '';
    // reset the cached search token if this is not either THE table, or its child details table
    if (sitName !== tableName && `${sitName}_details` !== tableName) {
      // reset values if the tables are different
      this.props.searchInputFunction('', tableName);
    }
    this.filterData();
    window.addEventListener('scroll', this.handleScroll, true);
    window.addEventListener('resize', this.updateWidths, true);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll, true);
    window.removeEventListener('resize', this.updateWidths, true);
  }

  componentDidUpdate(prevProps, prevState) {
    const { data } = this.props;
    const prevData = prevProps.data;
    if (prevProps.isLoading !== this.props.isLoading) {
      // if we get new data, then scroll position might change, which could leave an awkward hanging fixed header.
      // so to be safe if data changes, re-attach the header
      this.handleUnFixed();
    }
    if (!isEqual(data, prevData)) {
      // if we get new data, then scroll position might change, which could leave an awkward hanging fixed header.
      // so to be safe if data changes, re-attach the header
      this.handleUnFixed();

      if (!data || !data.length > 0) {
        this.setState({
          originalData: [],
          searchedData: [],
          sortedData: [],
          displayData: null
        });
      } else {
        this.setState(
          {
            pageNumber: 1,
            originalData: data,
            needToSort: true
          },
          () => {
            this.filterData();
          }
        );
      }
    }
  }

  validateImportantProps = () => {
    const {
      ErrorHandler,
      searchColumns,
      columns,
      data,
      defaultSort
    } = this.props;
    if (!data || !data.length) {
      return;
    }

    const sampleRow = data && data.length && data.length > 0 && data[0];

    // Validate column dataKeys
    columns.forEach((col) => {
      if (!sampleRow || !checkNestedKeys(col.dataKey, sampleRow)) {
        ErrorHandler.bug(
          `ERROR::DataTable::dataKey ${col.dataKey} is not valid!`
        );
      }
    });

    // Validate SearchColumns
    if (searchColumns && searchColumns.length > 0) {
      searchColumns.forEach((searchCol) => {
        if (!checkNestedKeys(searchCol, sampleRow)) {
          ErrorHandler.bug(
            `ERROR::DataTable::SearchColumn ${searchCol} is not valid!`
          );
        }
      });
    }

    // Validate defaultSort column only if a defaultSort has been provided
    if (defaultSort && !columns.find((col) => col.dataKey === defaultSort)) {
      ErrorHandler.bug(
        `ERROR::DataTable::defaultSort ${defaultSort} is not valid!`
      );
    }
  };

  //   --initial load--
  //   data ==>> searchFilter ==>> sort ==>> paginate ==>> display
  //   --on search--
  //   data ==>> searchFilter ==>> sort ==>> paginate ==>> display
  //   --on sort--
  //   searchData ==>> sort ==>> paginate ==>> display
  //   --on page--
  //   sortedData ==>> paginate ==>> display

  filterData = () => {
    // ensure the header is fixed if its updating
    this.handleUnFixed();

    this.validateImportantProps();

    const { searchColumns, columns } = this.props;
    const { searchQuery, originalData } = this.state;
    // if there is no search term OR if there are no search columns return all originalData
    if (!searchColumns || !searchColumns.length > 0 || !searchQuery) {
      if (!this.state.needToSort) {
        this.setState({
          searchedData: originalData,
          sortedData: originalData
        });
      } else {
        this.setState({ searchedData: originalData }, () => {
          this.handleSort();
        });
      }
      return;
    }

    const searchedData = originalData.filter((row) => {
      let keep = false;
      for (const col of searchColumns) {
        const colConfig = columns.find(
          (c) => c.key === col || c.dataKey === col
        );
        // Here instead of just getting the raw value from the data, we should format it similar to how it appears on
        // the view so that people can search specifically what they are seeing... specially if we are formatting some
        // array or nested values.
        let val =
          colConfig &&
          colConfig.formatter &&
          colConfig.formatter(getNestedValue(col, row), row);

        // if we didn't get a string from formatting it, then fallback to the data value
        if (!val || typeof val !== 'string') {
          val = getNestedValue(col, row);
          // if the value is an array, join it into a string so we can search on it
          val = Array.isArray(val) ? val.join('') : val;
        }

        if (
          val &&
          typeof val === 'string' &&
          val.toLowerCase().includes(searchQuery.toLowerCase())
        ) {
          keep = true;
          break;
        }
      }
      return keep;
    });

    if (!this.state.needToSort) {
      this.setState({ searchedData, sortedData: searchedData });
    } else {
      this.setState({ searchedData }, () => {
        this.handleSort();
      });
    }
  };

  prepFieldForSort = (sortType, val, column) => {
    const { ErrorHandler } = this.props;
    let aVal = val;
    switch (sortType) {
      case 'number':
        if (typeof aVal === 'string') {
          aVal = parseFloat(aVal.replace(/[^\d.-]/g, ''));
        }
        if (!aVal) {
          aVal = 0;
        }
        break;
      case 'boolean':
        // should just work
        break;
      case 'string':
        aVal = typeof aVal === 'string' ? aVal.toLowerCase() : aVal;
        break;
      case 'date':
      case 'time':
        aVal = new Date(aVal).getTime();
        break;
      case 'array':
        // if we try to sort a column of arrays, just join the arrays into a big string, and string compare...
        // lets hope they have the values in the order they want. may consider sorting the strings first??
        aVal = Array.isArray(aVal) ? aVal.join('') : '';
        break;
      case 'object':
        // I don't know why anyone would try to sort by objects,
        // but if they do, we will just take the values in order
        // mash them into a string, then compare the strings. lol
        aVal = aVal ? Object.values(aVal).join('') : '';
        ErrorHandler.bug(
          'Attempting to sort a column of objects... are you sure you wired the data up right?'
        );
        break;
      default:
        ErrorHandler.bug(
          `ERROR::DataTable::Invalid type [${sortType}] provided for datatable column [${column}], sample value: ${JSON.stringify(
            aVal
          )}`
        );
    }
    return aVal;
  };

  handleSort = (dataKey) => {
    let column = dataKey;

    const {
      createEvent,
      eventTypes,
      tableName,
      columns,
      secondarySort
    } = this.props;
    const {
      sortColumn,
      sortDirection,
      searchedData,
      shouldSendAnalytics
    } = this.state;

    let direction = sortDirection;

    if (dataKey) {
      // if we are passing a key, then its cause we clicked a header,
      // so we need to handle direction change if needed
      if (dataKey === sortColumn) {
        // if the column passed is the same as the currently sorted column, then switch direction
        direction = direction === 'ASC' ? 'DESC' : 'ASC';
        this.setState({ sortDirection: direction });
      }
      this.setState({ sortColumn: dataKey });
    } else {
      column = sortColumn;
    }

    if (shouldSendAnalytics) {
      // Analytics
      createEvent &&
        typeof createEvent === 'function' &&
        createEvent({
          action: eventTypes && eventTypes.TABLE_SORT,
          detail: `${tableName}/${dataKey}:${direction}`
        });
    }

    // find the item from the columns so that we can find its 'type'
    // also maybe we should allow the user to pass a sort function
    // on second thought, no, lets just keep expanding the sort types. we can have nice custom ones.

    // if dataKey is undefined, then we are doing the default sort, so use that as the key
    const sortItem = columns.find((col) => col.dataKey === column);
    let sortType = sortItem && sortItem.type;

    let sortedData = [];

    //gotchas  null values
    // mixed types?
    sortedData = searchedData.sort((a, b) => {
      let aVal = getNestedValue(column, a) || '';
      let bVal = getNestedValue(column, b) || '';
      let cVal = '';
      let dVal = '';

      if (secondarySort) {
        cVal = getNestedValue(secondarySort, a) || '';
        dVal = getNestedValue(secondarySort, b) || '';
      }

      // if a column sort type was not provided, try to get it manually
      sortType = sortType || typeof aVal;
      if (sortType === 'object') {
        sortType = Array.isArray(aVal) ? 'array' : 'object';
      }

      aVal = this.prepFieldForSort(sortType, aVal, column);
      bVal = this.prepFieldForSort(sortType, bVal, column);
      if (secondarySort) {
        let secondarySortType = typeof cVal;
        if (secondarySortType === 'object') {
          secondarySortType = Array.isArray(aVal) ? 'array' : 'object';
        }
        cVal = this.prepFieldForSort(secondarySortType, cVal, column);
        dVal = this.prepFieldForSort(secondarySortType, dVal, column);
      }

      const compareVals = (a, b, shouldSecondarySort) => {
        let result = 0;

        if (a < b) {
          result = 1;
        } else if (a > b) {
          result = -1;
        } else if (shouldSecondarySort) {
          compareVals(cVal, dVal, false);
        }

        return result;
      };

      return direction === 'DESC'
        ? compareVals(aVal, bVal, secondarySort)
        : compareVals(bVal, aVal, secondarySort);
    });

    this.setState(
      {
        sortedData,
        needToSort: false,
        shouldSendAnalytics: false
      },
      () => {
        this.updateWidths();
      }
    );
  };

  handleColumnHeaderClick = (dataKey) => {
    this.setState({ shouldSendAnalytics: true }, () => {
      this.handleSort(dataKey);
    });
  };

  handlePageChange = (page) => {
    this.tableTopScrollRef.current.scrollIntoView({
      block: 'start'
    });
    this.setState({ pageNumber: page });
  };

  handleDebouncedSearchQueryAnalyticsLogging = debounce(() => {
    const { tableName, createEvent, eventTypes } = this.props;
    const { searchQuery } = this.state;
    createEvent &&
      typeof createEvent === 'function' &&
      createEvent({
        action: eventTypes && eventTypes.TEXT_FILTER,
        detail: `${tableName}:${searchQuery}`
      });
  }, 1500);

  handleQueryChange = (e, value) => {
    this.handleDebouncedSearchQueryAnalyticsLogging();
    const searchQuery = value;
    if (typeof this.props.setQueryParams === 'function') {
      this.props.setQueryParams({ s: encodeURIComponent(value) });
    }
    this.setState({ searchQuery, pageNumber: 1 }, this.filterData);
  };

  handleRowClick = (row) => {
    if (this.props.onRowClick && typeof this.props.onRowClick === 'function') {
      this.props.searchInputFunction(
        this.state.searchQuery,
        this.props.tableName
      );
      this.props.onRowClick(row);
    }
  };

  handleScroll = (e) => {
    this.topOffset = 60;
    const rootEl = document.getElementById('root');

    const rootClasses = rootEl && rootEl.classList;
    const { offsets, scrollIds, minTablePixelWidth } = this.props;
    if (offsets && Array.isArray(offsets) && offsets.length > 0) {
      offsets.forEach((ofs) => {
        if (rootClasses.contains(ofs.className)) {
          this.topOffset += ofs.offset;
        }
      });
    }

    if (this.tableRef && this.tableRef.current) {
      const isScrollingHorizontal = e.target.id === scrollIds?.horizontal;
      const isScrollingVertical = e.target.id === scrollIds?.vertical;
      // if we are scrolling vertically
      if (isScrollingVertical) {
        // and if the scroll position is less than topOffset minus our height
        if (this.getPosition(this.tableRef.current) < this.topOffset - 140) {
          // and if its currently unfixed
          if (!this.state.isFixedHeader) {
            // THEN we can finally update state to fixed... lots of "if"s but it prevents superfluous updates
            this.setState({ isFixedHeader: true });
          }
        } else {
          if (this.state.isFixedHeader) {
            this.handleUnFixed();
          }
        }
      }
      if (isScrollingHorizontal) {
        this.updateHorizontalOffset();
      }
    }
    if (this.tableWrapperRef && this.tableWrapperRef.current) {
      const tableWidth = this.tableWrapperRef?.current?.offsetWidth || 0;
      const breakpoint = minTablePixelWidth || DEFAULT_CONTENT_MIN_WIDTH;
      if (tableWidth > breakpoint && this.state.needsHorizontalScroll) {
        //enable fixed header
        this.setState({ needsHorizontalScroll: false });
      }
      if (tableWidth < breakpoint && !this.state.needsHorizontalScroll) {
        //enable fixed header
        this.setState({ needsHorizontalScroll: true });
      }
    }
  };
  updateHorizontalOffset = () => {
    const { scrollIds } = this.props;
    const horizontalEl = document.getElementById(scrollIds.horizontal);
    const leftOffset = horizontalEl && horizontalEl.scrollLeft;
    this.setState({ leftOffset });
  };

  getPosition = (element) => {
    if (!element) {
      return;
    }
    let yPosition = 0;

    while (element) {
      yPosition += element.offsetTop - element.scrollTop + element.clientTop;
      element = element.offsetParent;
    }

    return yPosition;
  };

  updateWidths = () => {
    const widths = [];
    let els = document.getElementsByClassName('first-table-row');
    els = els && els.length > 0 && els[0].children;
    const count = (els && els.length) || 0;
    for (let i = 0; i < count; i++) {
      widths.push(els[i].offsetWidth);
    }
    this.setState({ widths });
  };

  handleUnFixed = () => {
    if (this.state.isFixedHeader) {
      this.setState({ isFixedHeader: false });
    }
  };

  handleFilterChange = ({ filteredData, isFiltered, totalCount }) => {
    this.setState({ originalData: filteredData, isFiltered, totalCount }, () =>
      this.filterData()
    );
  };

  render() {
    const {
      sortedData,
      originalData,
      pageNumber,
      sortColumn,
      sortDirection,
      searchQuery,
      widths,
      isFixedHeader,
      leftOffset,
      isFiltered,
      totalCount,
      needsHorizontalScroll
    } = this.state;

    const {
      pageName,
      columns,
      csvColumns,
      csvFilterCategories,
      columnTotals,
      className,
      isLoading,
      emptyStateMessage,
      noResultsMessage,
      title,
      subtitle,
      additionalFilters,
      searchColumns,
      tableProps,
      rowProps,
      searchPlaceholder,
      isNotFixed,
      dataType,
      isEmpty,
      minTablePixelWidth,
      data
    } = this.props;

    let displayData = [];
    let itemCount = 0;

    if (sortedData && sortedData.length > 0) {
      const startIndex = PAGE_SIZE * (pageNumber - 1);
      const endIndex = PAGE_SIZE * pageNumber;
      displayData =
        (sortedData &&
          sortedData.length > 0 &&
          sortedData.slice(startIndex, endIndex)) ||
        [];
      itemCount = sortedData.length;
    }

    const isNoData =
      isEmpty || !data || !Array.isArray(data) || data.length === 0;

    // if a callback function was passed as a prop for the onRowClick,
    // then the rows are clickable, and apply any applicable styles/functionality
    const isRowClickable = typeof this.props.onRowClick === 'function';

    const modifiedTitle = (
      <Fragment>
        {!!title && title}
        {isFiltered && typeof this.props.filterCountFormatter === 'function' ? (
          this.props.filterCountFormatter({ itemCount, totalCount })
        ) : (
          <span
            style={{
              display: 'inline-block',
              borderLeft: !!title ? '1px solid #a1a3a4' : 'none',
              fontSize: '12px',
              height: '19px',
              fontWeight: 'normal',
              color: '#a1a3a4',
              marginLeft: !!title ? '10px' : '0',
              paddingLeft: !!title ? '10px' : '0'
            }}>
            Showing {itemCount} of {totalCount} {dataType}
          </span>
        )}
      </Fragment>
    );

    return (
      <div className="DataTableWrapper" ref={this.tableWrapperRef}>
        <span
          style={
            { position: 'relative', top: '-100px' }
            /* yeah, this looks sketchy. basically need to scroll up 100px above the top of the table because of
             * overlapping hover titlebars.
             */
          }
          ref={this.tableTopScrollRef}></span>
        <Card
          className={`${className || ''}${modifiedTitle ? ' has-title' : ''}`}
          isLoading={isLoading}
          title={isNoData ? null : modifiedTitle}
          subtitle={isNoData ? null : subtitle}>
          <div className="DataTableV2" ref={this.tableRef}>
            {(!!additionalFilters || !isNoData) && (
              <div className="filterRow">
                {additionalFilters}
                {!!searchColumns && searchColumns.length > 0 && (
                  <SearchInput
                    onSearch={this.handleQueryChange}
                    value={searchQuery}
                    placeholder={searchPlaceholder || 'Search'}
                  />
                )}
                {csvFilterCategories && (
                  <CSVExport
                    pageName={pageName}
                    tableColumns={csvColumns || columns}
                    filterCategories={csvFilterCategories}
                    data={originalData}
                  />
                )}
              </div>
            )}
            {!isNoData && (
              <FilterBox
                setQueryParams={this.props.setQueryParams}
                queryParams={this.props.queryParams}
                ErrorHandler={this.props.ErrorHandler}
                page="/storybook"
                filterCategories={this.props.filterCategories}
                data={data}
                onChange={this.handleFilterChange}
              />
            )}
            {displayData.length ? (
              <div className="DataTableContent">
                <Table
                  basic="very"
                  sortable
                  selectable
                  fixed={!isNotFixed}
                  style={{
                    minWidth: minTablePixelWidth
                      ? `${minTablePixelWidth}px`
                      : `${DEFAULT_CONTENT_MIN_WIDTH}px`
                  }}
                  //style={isFixedHeader ? { paddingTop: '-10px' } : null}
                  {...tableProps}>
                  <Table.Header>
                    <Table.Row className="table-header-row">
                      {columns.map((d, index) => (
                        <Table.HeaderCell
                          className="table-header-cell"
                          key={index}
                          style={Object.assign(
                            isFixedHeader && !needsHorizontalScroll
                              ? { visibility: 'hidden' }
                              : {},
                            d.style,
                            d.headerStyle,
                            d.width ? { width: d.width } : {}
                          )}
                          sorted={
                            sortColumn === d.dataKey
                              ? sortDirection === 'DESC'
                                ? 'descending'
                                : 'ascending'
                              : null
                          }
                          onClick={() => {
                            this.handleColumnHeaderClick(d.dataKey);
                          }}>
                          <HeaderInfoTooltip content={d.description}>
                            <span className="title">
                              {d.name}{' '}
                              {columnTotals &&
                              columnTotals.hasOwnProperty(d.dataKey)
                                ? `(${numberWithCommas(
                                    columnTotals[d.dataKey]
                                  )})`
                                : null}
                            </span>
                          </HeaderInfoTooltip>
                        </Table.HeaderCell>
                      ))}
                    </Table.Row>
                    {isFixedHeader &&
                    !needsHorizontalScroll && ( // is fixed and no horizontal scroll
                        <Table.Row
                          className="table-header-row"
                          style={{
                            position: 'fixed',
                            top: `${this.topOffset}px`,
                            background: '#fff',
                            minWidth: '1160px',
                            display: 'flex',
                            zIndex: '3',
                            marginLeft: `-${leftOffset}px`
                          }}>
                          {columns.map((d, index) => (
                            <Table.HeaderCell
                              className="table-header-cell"
                              key={index}
                              style={Object.assign(
                                {
                                  width:
                                    d.width || (widths && widths[index])
                                      ? `${widths[index]}px`
                                      : null,
                                  display: 'inline-block'
                                },
                                d.style,
                                d.headerStyle
                              )}
                              sorted={
                                sortColumn === d.dataKey
                                  ? sortDirection === 'DESC'
                                    ? 'descending'
                                    : 'ascending'
                                  : null
                              }
                              onClick={() => {
                                this.handleColumnHeaderClick(d.dataKey);
                              }}>
                              <HeaderInfoTooltip content={d.description}>
                                <span className="title">
                                  {d.name}{' '}
                                  {columnTotals &&
                                  columnTotals.hasOwnProperty(d.dataKey)
                                    ? `(${numberWithCommas(
                                        columnTotals[d.dataKey]
                                      )})`
                                    : null}
                                </span>
                              </HeaderInfoTooltip>
                            </Table.HeaderCell>
                          ))}
                        </Table.Row>
                      )}
                  </Table.Header>
                  <Table.Body>
                    {displayData.map((row, index) => (
                      <Table.Row
                        {...rowProps}
                        key={index}
                        className={index === 0 ? 'first-table-row' : null}
                        style={
                          typeof this.props.rowStyleFormatter === 'function'
                            ? this.props.rowStyleFormatter(row, index)
                            : {}
                        }
                        onClick={() => {
                          this.handleRowClick(row);
                        }}>
                        {columns.map((col, colIndex) => (
                          <Table.Cell
                            {...(col.cellProps || {})}
                            key={colIndex}
                            style={Object.assign(
                              {
                                cursor: isRowClickable ? 'pointer' : 'default'
                              },
                              col.style,
                              col.cellStyle
                            )}>
                            {typeof col.formatter === 'function'
                              ? col.formatter(
                                  getNestedValue(col.dataKey, row),
                                  row
                                )
                              : getNestedValue(col.dataKey, row)}
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
                <TablePagination
                  activePage={pageNumber}
                  onPageChange={this.handlePageChange}
                  itemCount={itemCount}
                  perPage={PAGE_SIZE}
                />
              </div>
            ) : (
              <div
                className="well"
                style={{
                  marginLeft: '50%',
                  transform: 'translateX(-50%)'
                }}>
                {isNoData
                  ? emptyStateMessage || 'No data found.'
                  : noResultsMessage || 'No matches found'}
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }
}

export default DataTable;
